import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, Eye, EyeOff, LockKeyhole, ShieldCheck } from "lucide-react";
import type { Owner } from "../../types";
import { api } from "../../lib/api";
import Avatar from "../../components/Avatar";
import { LEGACY_PROFILES, type AvatarKey } from "../../lib/profiles";
import Brand from "../../components/Brand";
import LoadingLabel from "../../components/LoadingLabel";

export default function Login({ onLogin }: { onLogin: (owner: Owner) => void }) {
  const [selected, setSelected] = useState<Owner | null>(null),
    [pin, setPin] = useState(""),
    [show, setShow] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const [choices, setChoices] = useState<{ name: string; avatar: AvatarKey }[] | null>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    api<{ profiles: { name: string; avatar: AvatarKey }[] }>("login-profiles")
      .then((r) => setChoices(r.profiles.length ? r.profiles : LEGACY_PROFILES))
      .catch(() => setChoices(LEGACY_PROFILES));
  }, []);
  const avatarOf = (name: string) => choices?.find((p) => p.name === name)?.avatar;
  useEffect(() => {
    if (selected) input.current?.focus();
  }, [selected]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("login", { profile: selected, pin });
      onLogin(selected!);
    } catch (e) {
      setError((e as Error).message);
      setPin("");
      input.current?.focus();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <header>
        <Brand />
        <span className="private-pill">
          <LockKeyhole size={13} /> Just for us
        </span>
      </header>
      <main className="login-main">
        <div className="login-decoration">
          <span />
          <span />
          <span />
        </div>
        <p className="eyebrow">OUR LITTLE HOUSEHOLD</p>
        <h1>{selected ? `Hey, ${selected}.` : "Who’s checking in?"}</h1>
        <p className="muted login-intro">
          {selected
            ? "Your passcode, then you’re home."
            : "A little clarity for everything we spend."}
        </p>
        {!selected ? (
          <div className="profile-choices">
            {(choices ?? []).map(({ name: p, avatar }) => (
              <button
                className="profile-choice"
                key={p}
                onClick={() => {
                  setSelected(p);
                  setError("");
                }}
              >
                <Avatar owner={p} avatar={avatar} size={100} />
                <strong>{p}</strong>
                <span>
                  Let’s have a look <ArrowRight size={14} />
                </span>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={submit} className="pin-form">
            <Avatar owner={selected} avatar={avatarOf(selected)} size={72} />
            <label htmlFor="pin">Six-digit passcode</label>
            <div className="pin-field">
              <input
                ref={input}
                id="pin"
                autoComplete="current-password"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                type={show ? "text" : "password"}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="······"
                required
                aria-describedby={error ? "login-error" : undefined}
              />
              <button
                type="button"
                className="icon-button"
                aria-label={show ? "Hide passcode" : "Show passcode"}
                onClick={() => setShow(!show)}
              >
                {show ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
            {error && (
              <p className="form-error" id="login-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="primary-button"
              disabled={pin.length !== 6 || busy}
            >
              {busy ? <LoadingLabel>Opening</LoadingLabel> : "Open my dashboard"}
              <ArrowRight size={18} />
            </button>
            <p className="remember-note">
              <ShieldCheck size={16} /> Remembered on this device for 90 days
            </p>
            <button
              className="text-button"
              type="button"
              onClick={() => {
                setSelected(null);
                setPin("");
                setError("");
              }}
            >
              <ArrowLeft size={14} /> Choose another profile
            </button>
          </form>
        )}
        <div className="login-bottom">
          <span className="tiny-line" />
          One home. The whole picture.
          <span className="tiny-line" />
        </div>
      </main>
      <footer>Private by default. Shared between you two.</footer>
    </div>
  );
}
